/**
 * Buyer: review submitted QA inspection report (quotation-context, non-auction, non-CM).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { API_URLS } from '../../../constants/api-urls';
import '../../../assets/styles/buyer_qa_inspection_review.scss';

function isCmQaRequest(qr) {
  if (!qr) return false;
  if (qr.context_source === 'contract_manufacturer') return true;
  const cmRfq = qr.cm_rfq_id != null && Number(qr.cm_rfq_id) > 0;
  const cmQ = qr.cm_quotation_id != null && Number(qr.cm_quotation_id) > 0;
  return cmRfq && cmQ;
}

function parseInspectionReport(raw) {
  if (!raw) return { items: [] };
  try {
    const report = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const items = Array.isArray(report?.items) ? report.items : [];
    return { items, ...report };
  } catch {
    return { items: [] };
  }
}

function formatStatusLabel(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'passed') return 'Passed';
  if (s === 'minor_issues') return 'Minor issues';
  if (s === 'failed') return 'Failed';
  return status || '—';
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export default function BuyerQAInspectionReview() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [qaRequest, setQaRequest] = useState(null);
  const [decision, setDecision] = useState(null);
  const [inspectorName, setInspectorName] = useState('Quality Inspector');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (!inspectionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const [inspRes, decisionRes] = await Promise.all([
          CommonService.get(API_URLS.QA.INSPECTION_BY_ID(inspectionId)),
          CommonService.get(API_URLS.QA.DECISION_BY_INSPECTION(inspectionId)).catch(() => null),
        ]);
        if (cancelled) return;

        const insp = inspRes?.data ?? null;
        if (!insp) {
          toast.error('Inspection not found');
          setLoading(false);
          return;
        }
        setInspection(insp);

        const qrRes = await CommonService.get(API_URLS.QA.GET_BY_ID(insp.qa_request_id));
        const qr = qrRes?.data ?? null;
        if (cancelled) return;
        setQaRequest(qr);

        if (isCmQaRequest(qr)) {
          toast.info('Contract manufacturing QA uses the CM quality approval workspace.');
          navigate('/buyer/qa-quotations-list', { replace: true });
          return;
        }
        if (qr?.context_type === 'auction' && qr?.auction_id) {
          toast.info('Auction QA uses the auction accept/reject product flow.');
          navigate(`/auction/${qr.auction_id}/accept-reject-product`, { replace: true });
          return;
        }

        const dec = decisionRes?.data ?? null;
        setDecision(dec);

        try {
          const profileRes = await CommonService.get(
            API_URLS.QA.INSPECTOR_PROFILE_BY_USER(insp.inspector_user_id),
          );
          const profile = profileRes?.data ?? profileRes ?? {};
          setInspectorName(profile.company_name || profile.name || 'Quality Inspector');
        } catch (_) {
          setInspectorName('Quality Inspector');
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.message || 'Failed to load inspection report');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inspectionId, navigate]);

  const report = useMemo(
    () => parseInspectionReport(inspection?.inspection_report),
    [inspection?.inspection_report],
  );

  const isCompleted = String(inspection?.status || '').trim().toLowerCase() === 'completed';
  const hasDecision = Boolean(decision?.decision);
  const canDecide = isCompleted && !hasDecision;

  const productLabel =
    qaRequest?.scope_logistics?.product_name ||
    qaRequest?.scope_logistics?.product_description ||
    '—';

  const handleSubmitDecision = async (decisionValue) => {
    if (!canDecide || submitting) return;
    if (decisionValue === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejecting the inspection report.');
      return;
    }
    setSubmitting(true);
    try {
      await CommonService.post(API_URLS.QA.DECISION_CREATE, {
        inspection_id: Number(inspectionId),
        decision: decisionValue,
        rejection_reason: decisionValue === 'reject' ? rejectionReason.trim() : undefined,
      });
      toast.success(
        decisionValue === 'accept'
          ? 'Inspection report accepted. Escrow release can proceed per your organisation process.'
          : 'Inspection report rejected.',
      );
      const decisionRes = await CommonService.get(API_URLS.QA.DECISION_BY_INSPECTION(inspectionId));
      setDecision(decisionRes?.data ?? { decision: decisionValue });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to save decision');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="buyer-qa-inspection-review">
        <div className="buyer-qa-inspection-review__loading">Loading inspection report…</div>
      </div>
    );
  }

  if (!inspection || !qaRequest) {
    return (
      <div className="buyer-qa-inspection-review">
        <div className="buyer-qa-inspection-review__error">Inspection report is not available.</div>
      </div>
    );
  }

  return (
    <div className="buyer-qa-inspection-review">
      <div className="buyer-qa-inspection-review__container">
        <button
          type="button"
          className="buyer-qa-inspection-review__back"
          onClick={() => navigate('/buyer/qa-quotations-list')}
        >
          ← Back to QA quotations
        </button>

        <header className="buyer-qa-inspection-review__header">
          <h1>Review Inspection Report</h1>
          <p>
            {qaRequest.qa_request_number || `QA-${qaRequest.qa_request_id}`} · Supplier quotation QA
          </p>
        </header>

        {!isCompleted ? (
          <div className="buyer-qa-inspection-review__banner buyer-qa-inspection-review__banner--pending" role="status">
            The inspector has not submitted the final report yet. Check back once the inspection is marked
            completed.
          </div>
        ) : null}

        {hasDecision ? (
          <div
            className={`buyer-qa-inspection-review__banner buyer-qa-inspection-review__banner--${
              decision.decision === 'accept' ? 'accepted' : 'rejected'
            }`}
            role="status"
          >
            You {decision.decision === 'accept' ? 'accepted' : 'rejected'} this inspection report
            {decision.decision_made_at ? ` on ${formatDate(decision.decision_made_at)}` : ''}.
            {decision.decision === 'reject' && decision.rejection_reason ? (
              <span className="buyer-qa-inspection-review__banner-reason">
                Reason: {decision.rejection_reason}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="buyer-qa-inspection-review__layout">
          <section className="buyer-qa-inspection-review__card">
            <h2>Request summary</h2>
            <dl className="buyer-qa-inspection-review__meta">
              <div>
                <dt>Product</dt>
                <dd>{productLabel}</dd>
              </div>
              <div>
                <dt>Inspector</dt>
                <dd>{inspectorName}</dd>
              </div>
              <div>
                <dt>Inspection date</dt>
                <dd>{formatDate(inspection.inspection_date)}</dd>
              </div>
              <div>
                <dt>Report submitted</dt>
                <dd>{formatDate(inspection.completed_at || inspection.updated_at)}</dd>
              </div>
              <div>
                <dt>Overall rating</dt>
                <dd>{inspection.overall_rating || '—'}</dd>
              </div>
            </dl>
          </section>

          <section className="buyer-qa-inspection-review__card buyer-qa-inspection-review__card--wide">
            <h2>Inspection report items</h2>
            {report.items.length === 0 ? (
              <p className="buyer-qa-inspection-review__empty">No line items were recorded in the report.</p>
            ) : (
              <div className="buyer-qa-inspection-review__items">
                {report.items.map((item, index) => (
                  <div key={index} className="buyer-qa-inspection-review__item">
                    <div className="buyer-qa-inspection-review__item-head">
                      <span className="buyer-qa-inspection-review__item-name">
                        {item.name || `Item ${index + 1}`}
                      </span>
                      <span
                        className={`buyer-qa-inspection-review__item-status buyer-qa-inspection-review__item-status--${String(
                          item.status || 'passed',
                        ).replace(/_/g, '-')}`}
                      >
                        {formatStatusLabel(item.status)}
                      </span>
                    </div>
                    {item.notes ? (
                      <p className="buyer-qa-inspection-review__item-notes">{item.notes}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </section>

          {inspection.inspector_recommendations ? (
            <section className="buyer-qa-inspection-review__card buyer-qa-inspection-review__card--wide">
              <h2>Inspector recommendations</h2>
              <p className="buyer-qa-inspection-review__recommendations">
                {inspection.inspector_recommendations}
              </p>
            </section>
          ) : null}

          {canDecide ? (
            <section className="buyer-qa-inspection-review__card buyer-qa-inspection-review__card--wide buyer-qa-inspection-review__decision">
              <h2>Your decision</h2>
              <p className="buyer-qa-inspection-review__decision-help">
                Accept the report to confirm the inspection outcome and allow escrow release to the inspector.
                Reject if the report does not meet your requirements.
              </p>
              <label className="buyer-qa-inspection-review__reject-label" htmlFor="rejection-reason">
                Rejection reason (required if you reject)
              </label>
              <textarea
                id="rejection-reason"
                className="buyer-qa-inspection-review__reject-input"
                rows={4}
                placeholder="Describe why the inspection report is not acceptable…"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
              <div className="buyer-qa-inspection-review__decision-actions">
                <button
                  type="button"
                  className="buyer-qa-inspection-review__btn buyer-qa-inspection-review__btn--reject"
                  disabled={submitting}
                  onClick={() => handleSubmitDecision('reject')}
                >
                  {submitting ? 'Saving…' : 'Reject report'}
                </button>
                <button
                  type="button"
                  className="buyer-qa-inspection-review__btn buyer-qa-inspection-review__btn--accept"
                  disabled={submitting}
                  onClick={() => handleSubmitDecision('accept')}
                >
                  {submitting ? 'Saving…' : 'Accept report'}
                </button>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
